import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import { Heading, Icon, IconType, Paper } from 'lucid-components-react';
import React, { FC, useContext } from 'react';

import Link from 'components/link/link';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_NodeIndividualRole,
  Drupal_NodeRolesHub,
  Drupal_ParagraphPaper
} from 'generated/graphql';
import { isDefined } from 'types/types';

import './paper-hub-list.scss';

interface Props {
  paper: Drupal_NodeRolesHub[] | null;
  individualRole?: Drupal_NodeIndividualRole[] | null;
  noContainer?: boolean;
  withText?: boolean;
}

const PaperHubListHeading: FC = ({ children }) => (
  <Heading
    as="h2"
    flex={{
      default: '0 1 80%',
      mobile: '0 1 60%',
      tabletLandscape: '0 1 30%'
    }}
    mb="0"
    variant="subheadS"
  >
    {children}
  </Heading>
);

const PaperHubListItemMobile = ({
  url,
  title
}: {
  url: string;
  title: string;
}) => (
  <Link block className="paper-hub-list__link" to={url} underline="none">
    <div className="lucid-container--flex">
      <PaperHubListHeading>{title}</PaperHubListHeading>
      <p className="paper-hub-list__cta">
        <span className="paper-hub-list__cta-mobile-arrow">
          <Icon type={IconType.ArrowRight} />
        </span>
      </p>
    </div>
  </Link>
  );

const PaperHubList = ({
  paper,
  noContainer,
  individualRole,
  withText = false
}: Props) => {
  const { language } = useContext(LayoutContext);
  const { i18nKey } = language;

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_learn_more
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  return (
    <section
      className={`paper-hub-list ${noContainer ? '' : 'lucid-container'}`}
    >
      <div className="bottom-spacer-lg">
        <Paper
          as="section"
          className="card--raised-mobile  paper-hub-list--offset-top-lg  paper-hub-list--offset-top-sm-mobile"
          p={0}
          variant="raised"
        >
          <div className="card__contents">
            <ul className="paper-hub-list__wrap">
              {paper!.filter(isDefined).map((item, index) => {
                const rolesHubContent =
                  item.fieldRolesHubContent && item.fieldRolesHubContent.entity
                    ? (item.fieldRolesHubContent.entity
                      .entityTranslation as Drupal_ParagraphPaper)
                    : null;
                const rolesHubUrl =
                  item.entityUrl && item.entityUrl.path
                    ? item.entityUrl.path
                    : '';
                return (
                  rolesHubContent && (
                    <li
                      key={
                        rolesHubContent.fieldTitle ||
                        `paper-hub-list-item-${index}`
                      }
                      className="paper-hub-list__item"
                    >
                      <div className="paper-hub-list__content">
                        {withText ? (
                          <Heading as="h2" mb="0" variant="subheadS">
                            {rolesHubContent.fieldTitle}
                          </Heading>
                        ) : (
                          <PaperHubListHeading>
                            {rolesHubContent.fieldTitle}
                          </PaperHubListHeading>
                          )}
                        {rolesHubContent.fieldSubtitle && (
                          <p className="paper-hub-list__text">
                            {rolesHubContent.fieldSubtitle}
                          </p>
                        )}
                        <p className="paper-hub-list__cta">
                          <Link
                            hasArrow
                            secondary
                            className="paper-hub-list__cta-link"
                            to={rolesHubUrl}
                          >
                            {i18n('mktg-learn-more')}
                          </Link>
                        </p>
                      </div>
                      <PaperHubListItemMobile
                        title={rolesHubContent.fieldTitle!}
                        url={rolesHubUrl}
                      />
                    </li>
                  )
                );
              })}
              {individualRole &&
                individualRole.map((item, index) => (
                  <li
                    key={
                      item.title || `paper-hub-list-item-individual-${index}`
                    }
                    className="paper-hub-list__item"
                  >
                    <div className="lucid-container--flex hide-mobile">
                      {withText ? (
                        <Heading as="h2" variant="subheadS">
                          {item.title}
                        </Heading>
                      ) : (
                        <PaperHubListHeading>{item.title}</PaperHubListHeading>
                        )}
                      <p className="paper-hub-list__cta">
                        <span className="paper-hub-list__cta-text hide-mobile">
                          <Link
                            hasArrow
                            secondary
                            className="paper-hub-list__cta-link"
                            to={
                              item.entityUrl && item.entityUrl.path
                                ? item.entityUrl.path
                                : ''
                            }
                          >
                            {i18n('mktg-learn-more')}
                          </Link>
                        </span>
                        <span className="show-mobile paper-hub-list__cta-mobile-arrow">
                          <Icon type={IconType.ArrowRight} />
                        </span>
                      </p>
                    </div>
                    <PaperHubListItemMobile
                      title={item.title!}
                      url={
                        item.entityUrl && item.entityUrl.path
                          ? item.entityUrl.path
                          : ''
                      }
                    />
                  </li>
                ))}
            </ul>
          </div>
        </Paper>
      </div>
    </section>
  );
};

export default PaperHubList;
